/**
 * The Playground: chat against the draft before anyone sees it.
 *
 * It runs the **real engine** — the same `take_turn` a customer's message goes through — against
 * an unpublished config. That is the whole point: a simulation would answer the question "what did
 * I write?" when the question is "what will the bot do?".
 *
 * ## The JSON panel is not a debug afterthought
 *
 * Left is what a customer reads; right is the `ChatResponse` NAB receives. Seeing them side by
 * side is how "the placeholders are unfilled in the response and filled by NAB" stops being a
 * sentence in a document. The diagnostics below it answer the next question — *why* did it do that
 * — with the intent, the router's confidence, the validation verdict, and exactly what went to the
 * model.
 *
 * That last one is the screen's most useful line. Golden rule 2 says the LLM never sees customer
 * data, and here is the prompt, on screen, for an administrator to check rather than trust.
 */

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Card,
  EmptyState,
  Input,
  Select,
  StatusPill,
  directionOfText,
  playgroundChat,
  useToast,
  type PlaygroundTurn,
} from "@nab/shared";

import { useBot } from "../config";
import { VersionBar } from "./VersionBar";

interface Bubble {
  who: "customer" | "bot";
  text: string;
  step?: string;
}

export function PlaygroundPage(): JSX.Element {
  const { t } = useTranslation("app");
  const toast = useToast();
  const { draft, published, problem } = useBot();
  const [against, setAgainst] = useState<"draft" | "published">("draft");
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [turn, setTurn] = useState<PlaygroundTurn | null>(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionId = useRef<string | null>(null);
  /**
   * The history, stored the way the NAB Backend must store it: assistant turns as the templates
   * they came back as, placeholders unfilled (contract §3). The playground follows the same rule
   * so that what it exercises is the integration, not a friendlier version of it.
   */
  const history = useRef<{ role: string; content: string; nextStep?: string }[]>([]);

  async function send(text: string) {
    const clean = text.trim();
    if (!clean) return;
    setBubbles((current) => [...current, { who: "customer", text: clean }]);
    setMessage("");
    setBusy(true);
    try {
      const result = await playgroundChat({
        message: clean,
        sessionId: sessionId.current,
        history: history.current,
        against,
      });
      setTurn(result);

      const response = result.response as Record<string, unknown>;
      sessionId.current = String(response["sessionId"] ?? "") || sessionId.current;
      if (response["sessionReset"]) history.current = [];

      const step = String(response["nextStep"] ?? "");
      const said =
        typeof response["message"] === "string"
          ? response["message"]
          : firstMessage(response["messages"]);

      history.current = [
        ...history.current,
        { role: "user", content: clean },
        ...(said ? [{ role: "assistant", content: said, nextStep: step }] : []),
      ];
      setBubbles((current) => [
        ...current,
        { who: "bot", text: said || t("playground.noMessage"), step },
      ]);
    } catch {
      toast.show({ tone: "error", message: t("playground.failed") });
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    sessionId.current = null;
    history.current = [];
    setBubbles([]);
    setTurn(null);
  }

  if (problem) return <EmptyState title={t("notReady")} description={problem} />;

  const target = against === "draft" ? draft : published;

  return (
    <div className="flex flex-col">
      <VersionBar />
      <div className="grid gap-4 p-4 lg:grid-cols-2">
        {/* -- the phone ------------------------------------------------------------- */}
        <Card className="flex max-h-[70vh] flex-col p-0">
          <div className="flex items-center gap-2 border-b border-borderSubtle px-4 py-3">
            <Select
              label={t("playground.against")}
              value={against}
              onChange={(event) => {
                setAgainst(event.target.value as "draft" | "published");
                reset();
              }}
              options={[
                { value: "draft", label: t("playground.draft") },
                { value: "published", label: t("playground.published") },
              ]}
            />
            <StatusPill tone={against === "draft" ? "neutral" : "success"}>
              {target?.label ?? "—"}
            </StatusPill>
            <Button variant="secondary" size="sm" className="ms-auto" onClick={reset}>
              {t("playground.reset")}
            </Button>
          </div>

          <div className="flex-1 space-y-2 overflow-y-auto p-4">
            {bubbles.length === 0 ? (
              <p className="text-body-sm text-textMuted">{t("playground.empty")}</p>
            ) : null}
            {bubbles.map((bubble, index) => (
              <div
                key={index}
                dir={directionOfText(bubble.text)}
                className={
                  bubble.who === "customer"
                    ? "ms-auto max-w-[85%] rounded-md rounded-ee-sm bg-primary px-3 py-2 text-body-md text-onPrimary [unicode-bidi:isolate]"
                    : "me-auto max-w-[85%] rounded-md rounded-es-sm bg-surfaceSunken px-3 py-2 text-body-md text-textPrimary [unicode-bidi:isolate]"
                }
              >
                <span className="whitespace-pre-wrap">{bubble.text}</span>
                {bubble.step ? (
                  <span className="mt-1 block text-label-md text-textMuted">
                    {bubble.step}
                  </span>
                ) : null}
              </div>
            ))}
          </div>

          <form
            className="flex gap-2 border-t border-borderSubtle p-3"
            onSubmit={(event) => {
              event.preventDefault();
              void send(message);
            }}
          >
            <Input
              label={t("playground.message")}
              className="flex-1"
              value={message}
              placeholder={t("playground.placeholder")}
              onChange={(event) => setMessage(event.target.value)}
            />
            <Button type="submit" variant="primary" loading={busy} className="self-end">
              {t("playground.send")}
            </Button>
          </form>
        </Card>

        {/* -- what NAB receives ----------------------------------------------------- */}
        <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto">
          <Card className="p-4">
            <h3 className="mb-2 text-label-md uppercase tracking-wide text-textMuted">
              {t("playground.response")}
            </h3>
            <pre className="overflow-x-auto rounded-sm bg-surfaceSunken p-3 font-mono text-label-md">
              {turn ? JSON.stringify(turn.response, null, 2) : "—"}
            </pre>
          </Card>

          {turn ? (
            <Card className="flex flex-col gap-2 p-4">
              <h3 className="text-label-md uppercase tracking-wide text-textMuted">
                {t("playground.why")}
              </h3>
              <Fact label={t("playground.intent")} value={turn.diagnostics.intent} />
              <Fact
                label={t("playground.confidence")}
                value={
                  turn.diagnostics.routerFailed
                    ? t("playground.routerFailed")
                    : turn.diagnostics.routerConfidence?.toFixed(2) ?? null
                }
              />
              <Fact
                label={t("playground.validation")}
                value={turn.diagnostics.validation}
              />
              <Fact
                label={t("playground.language")}
                value={`${turn.diagnostics.language}-${turn.diagnostics.script}`}
              />
              {turn.diagnostics.collectionsSearched.length > 0 ? (
                <Fact
                  label={t("playground.collections")}
                  value={turn.diagnostics.collectionsSearched.join(", ")}
                />
              ) : null}

              <h3 className="mt-2 text-label-md uppercase tracking-wide text-textMuted">
                {/* Golden rule 2, on screen: an administrator can check what reached the model
                    rather than take our word for it. */}
                {t("playground.sentToModel")}
              </h3>
              <pre className="overflow-x-auto rounded-sm bg-surfaceSunken p-3 font-mono text-label-md">
                {turn.diagnostics.promptSentToModel
                  ? JSON.stringify(turn.diagnostics.promptSentToModel, null, 2)
                  : t("playground.noModelCall")}
              </pre>
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string | null }): JSX.Element {
  return (
    <div className="flex gap-2 text-body-sm">
      <span className="text-textMuted">{label}</span>
      <span className="font-mono">{value ?? "—"}</span>
    </div>
  );
}

/** A trigger has no `message`; its `messages.success` is what the app would most likely show. */
function firstMessage(messages: unknown): string {
  if (!messages || typeof messages !== "object") return "";
  const record = messages as Record<string, string>;
  return record["success"] ?? record["confirm"] ?? Object.values(record)[0] ?? "";
}
